import { useCookies } from '@vueuse/integrations/useCookies'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import dayjs from 'dayjs'
import { isEmpty } from 'lodash'
import { createBookingRequest } from '../store/modules/bookingRequest'
import { SESSION_DURATION } from '../constants'
import {
  formatDate,
  isAccommodationDateValid,
  replaceQueryParameter
} from '../system/helper'
import store from '../store'

export function useSessionManagement() {
  const { t } = useI18n()
  const router = useRouter()
  const route = useRoute()
  const cookies = useCookies([
    `sessionId_${store.state.hotelId}`,
    'bookingRequest',
    'tripadvisor_refid'
  ])

  // Watch for sessionId cookie removal
  const setupSessionWatcher = () => {
    let isHandlingExpiration = false

    cookies.addChangeListener((cookie) => {
      if (
        cookie.name === `sessionId_${store.state.hotelId}` &&
        !cookie.value &&
        !isHandlingExpiration
      ) {
        isHandlingExpiration = true
        handleSessionExpiration().finally(() => {
          isHandlingExpiration = false
        })
      }
    })
  }

  const isSessionActive = (routeName) => {
    // check if it's inside iframe
    if (window.self !== window.top) {
      return false
    }

    const disabledRoutes = [
      'AfterPay',
      'customization-form',
      'customer-account-privacy-policy',
      'customer-account-general-terms',
      'package',
      'PaymentCallback',
      'ReservationModify',
      'ReservationCancel',
      'ReservationPayment'
    ]

    return routeName ? !disabledRoutes.includes(routeName) : true
  }

  const clearSession = () => {
    cookies.remove(`sessionId_${store.state.hotelId}`)
    cookies.remove('bookingRequest')
  }

  const refreshSession = () => {
    const query = route?.query
    const sessionId = cookies.get('sessionId')
    const bookingRequestSaved = cookies.get('bookingRequest')
    let bookingRequest,
      checkIn = dayjs(),
      checkOut,
      promoCodeInput = '',
      nbAdults,
      nbChildren,
      nbInfants

    if (!isEmpty(query)) {
      const { refid } = query
      if (refid) {
        cookies.get('tripadvisor_refid') === refid
          ? ''
          : cookies.set('tripadvisor_refid', refid, { expires: '30D' })
      }
      ;({
        checkIn = dayjs(),
        checkOut,
        promoCode: promoCodeInput,
        nbAdults,
        nbChildren,
        nbInfants
      } = query)
    } else if (
      sessionId &&
      bookingRequestSaved &&
      sessionId === bookingRequestSaved.sessionId
    ) {
      bookingRequest = createBookingRequest(bookingRequestSaved)

      let savedCheckIn = bookingRequest.checkIn
      if (!isAccommodationDateValid(savedCheckIn)) {
        savedCheckIn = bookingRequest.checkIn = formatDate(
          dayjs().startOf('day')
        )
      }
      if (!isAccommodationDateValid(bookingRequest.checkOut, savedCheckIn)) {
        bookingRequest.checkOut = formatDate(dayjs(savedCheckIn).add(1, 'days'))
      }
    }

    if (!bookingRequest) {
      checkOut = checkOut ? checkOut : dayjs(checkIn).add(1, 'days')
      bookingRequest = createBookingRequest({
        checkIn: formatDate(checkIn),
        checkOut: formatDate(checkOut),
        promoCodeInput
      })

      const guestsBounds = store.getters.guestsBounds

      function updateGuests(guestType, value) {
        const { min, max } = guestsBounds[guestType]
        const nbGuests = Math.min(Math.max(parseInt(value), min), max)

        bookingRequest.roomStays[0][guestType] = nbGuests
        replaceQueryParameter(guestMap[guestType], nbGuests.toString())
      }

      const guestMap = {
        adults: 'nbAdults',
        children: 'nbChildren',
        infants: 'nbInfants'
      }

      if (nbAdults) updateGuests('adults', nbAdults)
      if (nbChildren) updateGuests('children', nbChildren)
      if (nbInfants) updateGuests('infants', nbInfants)
    }

    const hotelId = store.state.hotelId
    const timestamp = Date.now()
    const sessionKey = `sessionId_${hotelId}`
    const sessionValue = `${bookingRequest.sessionId}_${timestamp}`

    cookies.set(sessionKey, sessionValue, {
      expires: dayjs().add(SESSION_DURATION, 'minutes').toDate()
    })

    store.commit('UPDATE_BOOKING_REQUEST', bookingRequest)
    store.commit('SET_ALLOW_THANKS', false)
    store.commit('SET_ALLOW_CANCELLATION_CONFIRMATION', false)

    const order = store.getters.currentRoomStayGuests?.order
    if (order) {
      store.commit('ADD_ORDER_TO_ROOM_STAY', {
        order: {
          ...order,
          totalPrice: order.hotelTotalPrice,
          totalDiscount: order.hotelTotalDiscount
        },
        roomStayIndex: store.getters.currentRoom
      })
      store.commit('UPDATE_CURRENT_ROOM_STAY', {
        ...store.getters.currentRoomStayGuests.order
      })
    } else {
      submitSearchInfo()
    }
  }

  const handleSessionExpiration = async () => {
    if (!isSessionActive(route.name)) {
      return
    }

    const Swal = (await import('sweetalert2')).default
    const { isConfirmed } = await Swal.fire({
      width: '28em',
      icon: 'info',
      title: t('App#Session#Popup#Title Session expired'),
      text: t(
        'App#Session#Popup#Text Displayed prices and conditions are not valid anymore.'
      ),
      confirmButtonText: t('App#Button OK'),
      allowEscapeKey: false,
      allowOutsideClick: false
    })

    if (isConfirmed) {
      router.push({ name: 'home' }).then(() => location.reload())
    }
  }

  const submitSearchInfo = () => {
    const bookingRequest = store.getters.bookingRequest
    const currentRoomStayGuests = store.getters.currentRoomStayGuests
    if (!currentRoomStayGuests) {
      return
    }

    store.commit('UPDATE_BOOKING_REQUEST', bookingRequest)

    const { adults, children, infants } = currentRoomStayGuests
    const { checkIn, checkOut, nbNights, promoCodeInput } = bookingRequest

    store.commit('UPDATE_CURRENT_ROOM_STAY', {
      nbAdults: parseInt(adults),
      nbChildren: parseInt(children),
      nbInfants: parseInt(infants),
      startDate: checkIn,
      endDate: checkOut,
      promotionCode: promoCodeInput?.toUpperCase() || '',
      datesDiff: nbNights,
      services: []
    })

    store.commit('UPDATE_ORDER_INFO', {
      datesDiff: nbNights
    })

    store.dispatch('getRoomsAvailabilities')
  }

  const goHomeAndReload = async (doReload = false) => {
    if (route.name === 'home') {
      if (doReload) {
        window.location.reload()
      }
      return
    }

    await router.push({ name: 'home' })
    if (doReload) {
      window.location.reload()
    }
  }

  return {
    isSessionActive,
    clearSession,
    refreshSession,
    handleSessionExpiration,
    setupSessionWatcher,
    submitSearchInfo,
    goHomeAndReload
  }
}
